import {
  AI_GENERATED_SECTION_NAMES,
  getAIGeneratedFingerprint,
  getCopiedAIGeneratedSectionHash,
  isAIGeneratedSectionForEntity,
  mergeAIGeneratedSectionFingerprint,
  reportAIGeneratedChipFailure,
  stampAIGeneratedSections,
} from "./aiGenerated.js";

const isCopyValueEmpty = (value) => {
  if (value === undefined || value === null) return true;
  if (typeof value === "string") return value === "";
  if (Array.isArray(value)) return value.every(isCopyValueEmpty);
  if (typeof value === "object") {
    return Object.entries(value)
      .filter(([key]) => key !== "id")
      .every(([, nestedValue]) => isCopyValueEmpty(nestedValue));
  }
  return false;
};

const SET_VALUE_OPTIONS = { shouldDirty: true, shouldValidate: true };

const FORM_SECTIONS = new Set(["instantForm", "leadForm"]);

export const applyAIGeneratedSectionStamp = ({
  getValues,
  setValue,
  basePath,
  section,
  jobID,
  allowFormStamp = false,
}) => {
  if (
    !basePath ||
    !section ||
    !AI_GENERATED_SECTION_NAMES.has(section) ||
    typeof getValues !== "function" ||
    typeof setValue !== "function"
  ) {
    return;
  }
  if (FORM_SECTIONS.has(section) && !allowFormStamp) return;

  try {
    const entity = getValues(basePath) || {};
    const nextValue = getValues(`${basePath}.${section}`);
    const stamped = stampAIGeneratedSections({
      entity,
      sections: { [section]: nextValue },
      jobID,
    });
    const nextAI = stamped.entity?.aiGenerated;
    const previousSections = entity?.aiGenerated?.sections || {};
    const nextSections = nextAI?.sections || {};
    if (nextSections[section] === previousSections[section] && nextAI) {
      return;
    }
    if (!nextAI) return;
    setValue(`${basePath}.aiGenerated`, nextAI, SET_VALUE_OPTIONS);
  } catch {
    reportAIGeneratedChipFailure({
      functionName: "stampAIGeneratedSections",
      section,
    });
  }
};

export const stampRecommendedLeadFormTargets = ({
  getValues,
  setValue,
  targets = [],
}) => {
  targets.forEach((path) => {
    if (typeof path !== "string") return;
    const section = path.endsWith(".instantForm")
      ? "instantForm"
      : path.endsWith(".leadForm")
        ? "leadForm"
        : "";
    if (!section) return;
    applyAIGeneratedSectionStamp({
      getValues,
      setValue,
      basePath: path.slice(0, -(section.length + 1)),
      section,
      allowFormStamp: true,
    });
  });
};

export const readStoredSectionFingerprint = ({
  getValues,
  basePath,
  section,
}) => {
  if (typeof getValues !== "function" || !basePath || !section) {
    return undefined;
  }
  const stored = getValues(`${basePath}.aiGenerated`)?.sections?.[section];
  return typeof stored === "string" ? stored : undefined;
};

const withoutSectionStamp = (description) => ({
  description,
  aiGeneratedSection: null,
});

export const readGeneratedDescription = (response) => {
  if (typeof response === "string") {
    return withoutSectionStamp(response);
  }
  if (!response || typeof response !== "object") {
    return { description: "", aiGeneratedSection: undefined };
  }

  const description =
    typeof response.description === "string" ? response.description : "";

  if (
    !Object.prototype.hasOwnProperty.call(response, "aiGeneratedSection") ||
    response.aiGeneratedSection == null
  ) {
    return withoutSectionStamp(description);
  }

  const fragment = response.aiGeneratedSection;
  if (typeof fragment !== "object" || Array.isArray(fragment)) {
    return withoutSectionStamp(description);
  }

  const section = typeof fragment.section === "string" ? fragment.section : "";
  const fingerprint =
    typeof fragment.fingerprint === "string" ? fragment.fingerprint : "";

  return {
    description,
    aiGeneratedSection:
      section && fingerprint ? { section, fingerprint } : null,
  };
};

const FINGERPRINT_PATTERN = /^[0-9a-f]{8}$/;

export const applyGeneratedAIFragmentOrStamp = ({
  getValues,
  setValue,
  basePath,
  aiGeneratedSection,
  platform,
  entityType,
}) => {
  if (
    aiGeneratedSection == null ||
    typeof aiGeneratedSection !== "object" ||
    Array.isArray(aiGeneratedSection)
  ) {
    return;
  }

  const { section, fingerprint } = aiGeneratedSection;
  if (
    typeof fingerprint !== "string" ||
    !FINGERPRINT_PATTERN.test(fingerprint)
  ) {
    return;
  }
  if (
    !basePath ||
    typeof getValues !== "function" ||
    typeof setValue !== "function" ||
    !isAIGeneratedSectionForEntity({ platform, entityType, section })
  ) {
    return;
  }

  const entity = getValues(basePath) || {};
  const merged = mergeAIGeneratedSectionFingerprint({
    entity,
    section,
    fingerprint,
    platform,
    entityType,
  });
  if (merged.merged && merged.entity?.aiGenerated) {
    setValue(
      `${basePath}.aiGenerated`,
      merged.entity.aiGenerated,
      SET_VALUE_OPTIONS
    );
  }
};

export const copyAIGeneratedSectionStamp = ({
  getValues,
  setValue,
  sourceBasePath,
  destinationBasePath,
  section,
  platform,
  entityType,
}) => {
  if (
    !sourceBasePath ||
    !destinationBasePath ||
    !isAIGeneratedSectionForEntity({ platform, entityType, section }) ||
    sourceBasePath === destinationBasePath ||
    typeof getValues !== "function" ||
    typeof setValue !== "function"
  ) {
    return;
  }

  const hash = getCopiedAIGeneratedSectionHash({
    sourceEntity: getValues(sourceBasePath),
    section,
  });
  if (!hash) return;

  applyGeneratedAIFragmentOrStamp({
    getValues,
    setValue,
    basePath: destinationBasePath,
    aiGeneratedSection: { section, fingerprint: hash },
    platform,
    entityType,
  });
};

export const commitGeneratedSectionText = ({
  getValues,
  setValue,
  trigger,
  basePath,
  section,
  index,
  platform,
  entityType,
  response,
  onAfterCommit,
  triggerPath,
}) => {
  const generated = readGeneratedDescription(response);
  if (!generated.description) return false;

  setValue(`${basePath}.${section}.[${index}].value`, generated.description, {
    shouldValidate: true,
    shouldDirty: true,
  });
  applyGeneratedAIFragmentOrStamp({
    getValues,
    setValue,
    basePath,
    aiGeneratedSection: generated.aiGeneratedSection,
    platform,
    entityType,
  });
  if (typeof trigger === "function") {
    trigger(triggerPath || `${basePath}.${section}.[${index}].value`);
  }
  onAfterCommit?.();
  return true;
};

export const copySectionValue = ({
  setValue,
  getValues,
  path,
  value,
  sourceBasePath,
  section,
  platform,
  entityType,
}) => {
  if (isCopyValueEmpty(value)) return;

  setValue(path, value, SET_VALUE_OPTIONS);

  if (!getValues || !sourceBasePath || !section || typeof path !== "string") {
    return;
  }
  const suffix = `.${section}`;
  if (!path.endsWith(suffix)) return;

  copyAIGeneratedSectionStamp({
    getValues,
    setValue,
    sourceBasePath,
    destinationBasePath: path.slice(0, -suffix.length),
    section,
    platform,
    entityType,
  });
};

export const stampStoredSectionAsAIGenerated = ({
  getValues,
  setValue,
  basePath,
  section,
  platform,
  entityType,
}) => {
  if (
    !basePath ||
    !section ||
    typeof getValues !== "function" ||
    typeof setValue !== "function" ||
    !isAIGeneratedSectionForEntity({ platform, entityType, section })
  ) {
    return;
  }

  const fingerprint = getAIGeneratedFingerprint(
    getValues(`${basePath}.${section}`),
    section
  );
  applyGeneratedAIFragmentOrStamp({
    getValues,
    setValue,
    basePath,
    aiGeneratedSection: { section, fingerprint },
    platform,
    entityType,
  });
};
